import { type Day } from '@choco-admin/types';
import { Button, Card, CardBody, Chip } from '@heroui/react';
import React, { useState } from 'react';
import { FiClock, FiEdit2, FiInfo } from 'react-icons/fi';

import { useSupplierDeliverySettings } from '../../../../../hooks/useSupplierDeliverySettings';

import { DeliverySettingsModal } from './DeliverySettingsModal';
import {
  CUTOFF_TYPE_LABELS,
  DAYS,
  formatLeadTime,
  formatTime,
} from './deliverySettingsView';

interface DeliverySettingsSectionProps {
  supplierId: string;
}

/**
 * Read-only summary of the distributor's delivery schedule, edited in a modal —
 * the same pattern as the chat-level settings on the Chats page, which is where
 * anyone comparing the two screens starts.
 *
 * Nothing here writes to the distributor form. The modal owns the mutation, so
 * these fields cannot ride along with a profile save. See `DeliverySettingsModal`.
 */
export const DeliverySettingsSection: React.FC<
  DeliverySettingsSectionProps
> = ({ supplierId }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { mappedSettings } = useSupplierDeliverySettings({ supplierId });

  const hasSettings = Boolean(mappedSettings);

  // Cutoff type is aggregated per-day by the backend and collapses to
  // 'no_cutoff' when delivery days carry different cutoff settings. Surface that
  // as "Mixed" rather than mislabelling days that do have a cutoff.
  const hasAnyCutoff = DAYS.some(
    (day) =>
      mappedSettings?.deliveryDays[day] && mappedSettings.hasCutoffByDay[day]
  );
  const cutoffTypeLabel = ((): string => {
    if (!mappedSettings) return 'No cutoff';
    if (mappedSettings.cutoffType === 'no_cutoff' && hasAnyCutoff)
      return 'Mixed';
    return (
      CUTOFF_TYPE_LABELS[mappedSettings.cutoffType] ?? mappedSettings.cutoffType
    );
  })();

  const renderCutoffCell = (day: Day) => {
    if (!mappedSettings?.deliveryDays[day]) {
      return <span className="text-default-400">No delivery</span>;
    }
    // Per-day, not from the aggregate cutoffType: a distributor can have a
    // cutoff on some delivery days and none on others, and the aggregate reads
    // 'no_cutoff' for that whole mix.
    if (mappedSettings.hasCutoffByDay[day]) {
      return (
        <span className="text-default-600">
          {formatLeadTime(mappedSettings.cutoffDays[day])} ·{' '}
          {formatTime(mappedSettings.cutoffTimes[day])}
        </span>
      );
    }
    return <span className="text-default-400">No cutoff</span>;
  };

  return (
    <Card className="border border-default-200">
      <CardBody className="p-6">
        {!hasSettings && (
          <div className="mb-5 flex items-start gap-2 rounded-lg border border-warning-200 bg-warning-50 px-4 py-3">
            <FiInfo className="mt-0.5 h-4 w-4 shrink-0 text-warning-600" />
            <p className="text-sm text-warning-700">
              This distributor has no delivery schedule yet, so buyers are not
              held to any delivery days or cutoffs. Click Edit to set them.
            </p>
          </div>
        )}

        <div className="flex items-start justify-between gap-4">
          <div className="flex items-start gap-3 min-w-0">
            <div className="w-10 h-10 rounded-full bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center shrink-0">
              <FiClock className="w-5 h-5 text-primary-600 dark:text-primary-400" />
            </div>
            <div className="min-w-0">
              <h3 className="text-lg font-semibold text-foreground">
                Delivery Days & Cutoff Times
              </h3>
              <p className="text-sm text-default-500">
                Delivery days and cutoff times for this distributor. These
                settings apply for all products.
              </p>
            </div>
          </div>
          <Button
            type="button"
            size="sm"
            variant="flat"
            color="primary"
            startContent={<FiEdit2 className="w-4 h-4" />}
            onPress={() => setIsModalOpen(true)}
          >
            Edit
          </Button>
        </div>

        <div
          className={`mt-5 space-y-3 ${
            hasSettings ? '' : 'opacity-50 pointer-events-none select-none'
          }`}
        >
          <div className="flex items-center gap-2">
            <span className="text-xs font-medium uppercase tracking-wide text-default-400">
              Cutoff type
            </span>
            <Chip size="sm" variant="flat" color="default">
              {cutoffTypeLabel}
            </Chip>
          </div>

          <div className="rounded-lg border border-default-200 divide-y divide-default-200">
            {DAYS.map((day) => (
              <div
                key={day}
                className={`flex items-center justify-between px-4 py-2.5 text-sm ${
                  mappedSettings?.deliveryDays[day] ? '' : 'opacity-50'
                }`}
              >
                <span className="font-medium text-foreground">{day}</span>
                {renderCutoffCell(day)}
              </div>
            ))}
          </div>
        </div>
      </CardBody>

      <DeliverySettingsModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        supplierId={supplierId}
      />
    </Card>
  );
};
